import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Box,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Games as GamesIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import AdminLayout from '../../layouts/AdminLayout';
import JalonsComptesSection from '../../components/admin/JalonsComptesSection';
import JoueursActifsSection from '../../components/admin/JoueursActifsSection';
import NouvellesFamillesSection from '../../components/admin/NouvellesFamillesSection';
import ClientsPayantsSection from '../../components/admin/ClientsPayantsSection';
import SessionsJeuSection from '../../components/admin/SessionsJeuSection';
import PaliersAventureSection from '../../components/admin/PaliersAventureSection';
import { AdminService } from '../../services/adminService';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip as RechartsTooltip, 
  Legend, 
  CartesianGrid,
  BarChart,
  Bar
} from 'recharts';

export default function AdminStatsScreen() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardStats, setDashboardStats] = useState({});
  const [evolutionData, setEvolutionData] = useState([]);
  const [loadingEvolution, setLoadingEvolution] = useState(true);
  const [showAnonymous, setShowAnonymous] = useState(false);
  const [granularity, setGranularity] = useState('month');
  const [retentionSeries, setRetentionSeries] = useState([]);
  // Lissage des courbes de rétention : 1 = brut (un point par jour de cohorte).
  const [retentionLissage, setRetentionLissage] = useState(7);
  const [onboardingFunnel, setOnboardingFunnel] = useState(null);
  /** Segment de marché : familles (B2C) et établissements (B2B) ne se mélangent pas. */
  const [segment, setSegment] = useState('familles');
  const [loadingFunnel, setLoadingFunnel] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
    fetchOnboardingFunnel();
  }, [segment]);

  useEffect(() => {
    fetchUsersEvolution();
  }, [granularity]);

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const statsData = await AdminService.getDashboardStats(segment);
      setDashboardStats(statsData);
      setRetentionSeries(statsData.retentionSeries || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError('Impossible de charger les statistiques du tableau de bord');
      setLoading(false);
    }
  };

  const fetchUsersEvolution = async () => {
    setLoadingEvolution(true);
    try {
      const data = await AdminService.getUsersEvolution(granularity);
      setEvolutionData(data || []);
      setLoadingEvolution(false);
    } catch (err) {
      console.error('Error fetching users evolution:', err);
      setLoadingEvolution(false);
    }
  };

  const fetchOnboardingFunnel = async () => {
    setLoadingFunnel(true);
    try {
      const data = await AdminService.getOnboardingFunnel(30, segment);
      setOnboardingFunnel(data ?? null);
      setLoadingFunnel(false);
    } catch (err) {
      console.error('Error fetching onboarding funnel:', err);
      setLoadingFunnel(false);
    }
  };

  // Composant pour les cartes de statistiques rapides
  const StatsCard = ({ title, value, icon, color, description }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ 
            backgroundColor: `${color}.light`, 
            borderRadius: '50%', 
            p: 1.5, 
            mr: 2,
            display: 'flex' 
          }}>
            {icon}
          </Box>
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        </Box>
        <Typography variant="h3" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
          {value}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const formatRetentionValue = (pct) => {
    if (pct === null || pct === undefined) return '—';
    const num = typeof pct === 'number' ? pct : parseFloat(pct);
    if (Number.isNaN(num)) return '—';
    return `${num}%`;
  };

  const formatDateOnly = (value) => {
    if (!value) return '—';
    // cas le plus courant: 'YYYY-MM-DD'
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('fr-FR');
  };

  const RETENTION_COURBES = [
    { offset: 1, key: 'd1', name: 'J+1', color: '#9c27b0' },
    { offset: 7, key: 'd7', name: 'J+7', color: '#2196f3' },
    { offset: 30, key: 'd30', name: 'J+30', color: '#4caf50' },
    { offset: 60, key: 'd60', name: 'J+60', color: '#ff9800' },
  ];

  const RetentionChart = () => {
    // Un point par jour de cohorte et par offset. Le lissage somme retenus et
    // cohortes sur N jours AVANT le ratio (une moyenne de taux donnerait à une
    // cohorte d'une famille le poids d'une cohorte de vingt). Un jour sans
    // entrée n'a pas de taux : null, pas 0.
    const byDay = new Map();
    RETENTION_COURBES.forEach(({ offset, key }) => {
      const rows = (retentionSeries || []).filter((r) => r.dayOffset === offset);
      rows.forEach((row, i) => {
        const fenetre = rows.slice(Math.max(0, i - retentionLissage + 1), i + 1);
        const cohorte = fenetre.reduce((a, r) => a + r.cohortSize, 0);
        const retenus = fenetre.reduce((a, r) => a + r.retainedUsers, 0);
        if (!byDay.has(row.cohortDay)) byDay.set(row.cohortDay, { cohortDay: row.cohortDay });
        const entry = byDay.get(row.cohortDay);
        entry[key] = cohorte > 0 ? Math.round((retenus / cohorte) * 1000) / 10 : null;
        entry[`${key}Cohorte`] = cohorte;
      });
    });
    const data = Array.from(byDay.values()).sort((a, b) =>
      String(a.cohortDay).localeCompare(String(b.cohortDay)));

    return (
      <Card>
        <CardHeader
          title="Rétention des familles par jour d'entrée — 3 derniers mois"
          subheader={
            retentionLissage === 1
              ? "Brut : une cohorte par jour d'entrée ; une cohorte n'apparaît qu'une fois sa fenêtre de retour écoulée"
              : `Cumul glissant sur ${retentionLissage} jours de cohortes ; une cohorte n'apparaît qu'une fois sa fenêtre de retour écoulée`
          }
          avatar={<TrendingUpIcon color="primary" />}
          action={
            <ToggleButtonGroup
              value={retentionLissage}
              exclusive
              size="small"
              onChange={(e, v) => { if (v) setRetentionLissage(v); }}
              sx={{ pr: 1 }}
            >
              <ToggleButton value={1}>Par jour</ToggleButton>
              <ToggleButton value={7}>Lissé 7 j</ToggleButton>
              <ToggleButton value={30}>Lissé 30 j</ToggleButton>
            </ToggleButtonGroup>
          }
        />
        <CardContent>
          <Box sx={{ height: 360, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="cohortDay"
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                  tickFormatter={(v) => formatDateOnly(v)}
                />
                <YAxis domain={[0, 100]} unit="%" />
                <RechartsTooltip
                  formatter={(value, name, item) => {
                    const courbe = RETENTION_COURBES.find((c) => c.name === name);
                    const cohorte = courbe ? item.payload[`${courbe.key}Cohorte`] : null;
                    return [`${value} %${cohorte ? ` (${cohorte} familles)` : ''}`, name];
                  }}
                  labelFormatter={(label) => `Cohorte : ${formatDateOnly(label)}`}
                />
                <Legend />
                {RETENTION_COURBES.map(({ key, name, color }) => (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={key}
                    stroke={color}
                    strokeWidth={2}
                    dot={retentionLissage === 1 ? { r: 2 } : false}
                    connectNulls={false}
                    name={name}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Composant pour le graphique d'évolution des utilisateurs
  const UsersEvolutionChart = () => {
    const periodLabel = granularity === 'week' ? 'Semaine' : 'Mois';
    const title = granularity === 'week'
      ? 'Évolution des utilisateurs (52 dernières semaines)'
      : 'Évolution des utilisateurs (12 derniers mois)';

    const maxValue = evolutionData.length > 0
      ? Math.max(...evolutionData.flatMap(d => [
          d.totalActiveUsers || 0,
          showAnonymous ? (d.anonymousUsers || 0) : 0,
        ]))
      : 100;
    const yMax = Math.ceil(maxValue * 1.1) || 10;

    return (
      <Card>
        <CardHeader
          title={title}
          avatar={<TrendingUpIcon color="primary" />}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pr: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showAnonymous}
                    onChange={(e) => setShowAnonymous(e.target.checked)}
                    size="small"
                  />
                }
                label="Anonymes"
                sx={{ m: 0 }}
              />
              <ToggleButtonGroup
                value={granularity}
                exclusive
                onChange={(e, val) => { if (val) setGranularity(val); }}
                size="small"
              >
                <ToggleButton value="month">Mois</ToggleButton>
                <ToggleButton value="week">Semaines</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          }
        />
        <CardContent>
          {loadingEvolution ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 400, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={evolutionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="periodLabel"
                    tick={{ fontSize: 11 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, yMax]} allowDecimals={false} />
                  <RechartsTooltip
                    formatter={(value, name) => [value, getTooltipLabel(name)]}
                    labelFormatter={(label) => `${periodLabel}: ${label}`}
                  />
                  <Legend formatter={(value) => getLegendLabel(value)} />
                  <Line
                    type="monotone"
                    dataKey="totalActiveUsers"
                    stroke="#2196f3"
                    strokeWidth={3}
                    dot={{ fill: '#2196f3', r: 4 }}
                    name="totalActiveUsers"
                  />
                  <Line
                    type="monotone"
                    dataKey="usersLoggedInLast30Days"
                    stroke="#4caf50"
                    strokeWidth={2}
                    dot={{ fill: '#4caf50', r: 3 }}
                    name="usersLoggedInLast30Days"
                  />
                  <Line
                    type="monotone"
                    dataKey="usersPlayedLast30Days"
                    stroke="#ff9800"
                    strokeWidth={2}
                    dot={{ fill: '#ff9800', r: 3 }}
                    name="usersPlayedLast30Days"
                  />
                  <Line
                    type="monotone"
                    dataKey="newUsers"
                    stroke="#9c27b0"
                    strokeWidth={2}
                    dot={{ fill: '#9c27b0', r: 3 }}
                    name="newUsers"
                  />
                  {showAnonymous && (
                    <Line
                      type="monotone"
                      dataKey="anonymousUsers"
                      stroke="#f44336"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: '#f44336', r: 3 }}
                      name="anonymousUsers"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Composant pour le graphique des exercices par type
  const ExercisesByTypeChart = () => {
    const exerciseData = Object.entries(dashboardStats.exercisesByGame || {}).map(([gameType, count]) => ({
      gameType,
      count
    }));

    return (
      <Card>
        <CardHeader 
          title="Exercices par type de jeu" 
          avatar={<GamesIcon color="secondary" />}
        />
        <CardContent>
          <Box sx={{ height: 300, width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={exerciseData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="gameType" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis />
                <RechartsTooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };

  // Composant pour le tunnel d'onboarding
  const OnboardingFunnelChart = () => {
    return (
      <Card>
        <CardHeader
          title="Tunnel d'onboarding (30 derniers jours)"
          avatar={<SpeedIcon color="info" />}
          subheader="Jalons réellement franchis — inscription, compagnon créé, combats terminés, onboarding achevé"
        />
        <CardContent>
          {/* ⚠️ Les deux jalons de combat sont vides AVANT l'instrumentation : le
              contexte `onboarding_combat_1/2` n'est écrit que depuis cette date.
              Sans ce rappel on lit un abandon là où il n'y a qu'une absence de mesure. */}
          {onboardingFunnel?.instrumentedSince && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Les jalons « Chemin de forêt » et « Combat monstre » ne sont mesurés que depuis le{' '}
              <strong>
                {new Date(onboardingFunnel.instrumentedSince).toLocaleDateString('fr-FR')}
              </strong>{' '}
              — avant cette date ils sont vides par construction, pas par abandon. Un combat
              n'est compté qu'une fois <em>terminé</em> : le bouton « Passer » ne laisse aucune trace.
            </Alert>
          )}
          {loadingFunnel ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 500, width: '100%' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  layout="vertical"
                  data={onboardingFunnel?.steps ?? []}
                  margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis 
                    dataKey="stepName" 
                    type="category" 
                    tick={{ fontSize: 11 }}
                    width={150}
                  />
                  <RechartsTooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        const data = payload[0].payload;
                        return (
                          <Box sx={{ bgcolor: 'white', p: 1.5, border: '1px solid #ccc', borderRadius: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>{label}</Typography>
                            <Typography variant="body2">Comptes : <strong>{data.userCount}</strong></Typography>
                            <Typography variant="body2">
                              Part des inscriptions : <strong>{data.survivalRate}%</strong>
                            </Typography>
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="userCount" fill="#4caf50" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const getLegendLabel = (dataKey) => {
    const labels = {
      totalActiveUsers: 'Utilisateurs actifs',
      usersLoggedInLast30Days: 'Connectés (30j)',
      usersPlayedLast30Days: 'Ont joué (30j)',
      newUsers: 'Nouveaux utilisateurs',
      anonymousUsers: 'Anonymes (onboarding)'
    };
    return labels[dataKey] || dataKey;
  };

  const getTooltipLabel = (dataKey) => {
    const labels = {
      totalActiveUsers: 'Utilisateurs actifs',
      usersLoggedInLast30Days: 'Connectés dans les 30 derniers jours',
      usersPlayedLast30Days: 'Ont joué dans les 30 derniers jours',
      newUsers: 'Nouveaux utilisateurs',
      anonymousUsers: 'Anonymes (onboarding non terminé)'
    };
    return labels[dataKey] || dataKey;
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Statistiques détaillées
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Analyses et métriques approfondies de la plateforme
        </Typography>
        <ToggleButtonGroup
          value={segment}
          exclusive
          size="small"
          onChange={(_, v) => { if (v) setSegment(v); }}
          sx={{ mt: 2 }}
        >
          <ToggleButton value="familles">Familles (B2C)</ToggleButton>
          <ToggleButton value="etablissements">Établissements (B2B)</ToggleButton>
          <ToggleButton value="tout">Tout</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          Établissements : élèves d&apos;une classe, profils de jeu des enseignants, adultes d&apos;école. Toutes les statistiques ci-dessous suivent ce choix.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Cartes de statistiques rapides */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6} lg={3}>
          <StatsCard
            title="Total utilisateurs"
            value={dashboardStats.totalUsers || 0}
            icon={<PersonIcon color="primary" />}
            color="primary"
            description="Utilisateurs adultes (hors enfants rattachés)"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <StatsCard
            title="Utilisateurs actifs"
            value={dashboardStats.activeUsers || 0}
            icon={<PersonIcon color="success" />}
            color="success"
            description="Adultes actifs (hors enfants rattachés)"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <StatsCard
            title="Connexions récentes"
            value={dashboardStats.recentActiveUsers || 0}
            icon={<SpeedIcon color="info" />}
            color="info"
            description="Adultes, dernière semaine"
          />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <StatsCard
            title="Combats récents"
            value={dashboardStats.recentCombatsPlayed || 0}
            icon={<GamesIcon color="warning" />}
            color="warning"
            description="Dernière semaine"
          />
        </Grid>
      </Grid>

      {/* Clients payants : nouveaux par boutique, payants en cours, MRR, derniers achats */}
      <Box sx={{ mb: 4 }}>
        <ClientsPayantsSection segment={segment} />
      </Box>

      {/* Jalons des comptes : combat 1, onboarding, identifié, payant + rétention J+30 */}
      <Box sx={{ mb: 4 }}>
        <JalonsComptesSection segment={segment} />
      </Box>

      {/* Rétention familles */}
      <Typography variant="h5" component="h2" gutterBottom>
        Rétention des familles
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Retenue à J+1 = au moins une partie jouée par un membre de la famille le lendemain de son
        entrée. Retenue à J+N (7, 30, 60) = au moins une partie entre J+N et J+N+
        {(dashboardStats.retentionWindowDays || 7) - 1}.
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Rétention J+1"
            value={formatRetentionValue(dashboardStats.retention?.d1?.retentionRatePct)}
            icon={<TrendingUpIcon color="primary" />}
            color="primary"
            description={
              dashboardStats.retention?.d1
                ? `${dashboardStats.retention.d1.retainedUsers || 0} / ${dashboardStats.retention.d1.cohortSize || 0} familles (cohorte ${formatDateOnly(dashboardStats.retention.d1.cohortStart)} → ${formatDateOnly(dashboardStats.retention.d1.cohortEnd)})`
                : '—'
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Rétention J+7"
            value={formatRetentionValue(dashboardStats.retention?.d7?.retentionRatePct)}
            icon={<TrendingUpIcon color="primary" />}
            color="primary"
            description={
              dashboardStats.retention?.d7
                ? `${dashboardStats.retention.d7.retainedUsers || 0} / ${dashboardStats.retention.d7.cohortSize || 0} familles (cohorte ${formatDateOnly(dashboardStats.retention.d7.cohortStart)} → ${formatDateOnly(dashboardStats.retention.d7.cohortEnd)})`
                : '—'
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Rétention J+30"
            value={formatRetentionValue(dashboardStats.retention?.d30?.retentionRatePct)}
            icon={<TrendingUpIcon color="primary" />}
            color="primary"
            description={
              dashboardStats.retention?.d30
                ? `${dashboardStats.retention.d30.retainedUsers || 0} / ${dashboardStats.retention.d30.cohortSize || 0} familles (cohorte ${formatDateOnly(dashboardStats.retention.d30.cohortStart)} → ${formatDateOnly(dashboardStats.retention.d30.cohortEnd)})`
                : '—'
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <StatsCard
            title="Rétention ~2 mois (J+60)"
            value={formatRetentionValue(dashboardStats.retention?.d60?.retentionRatePct)}
            icon={<TrendingUpIcon color="primary" />}
            color="primary"
            description={
              dashboardStats.retention?.d60
                ? `${dashboardStats.retention.d60.retainedUsers || 0} / ${dashboardStats.retention.d60.cohortSize || 0} familles (cohorte ${formatDateOnly(dashboardStats.retention.d60.cohortStart)} → ${formatDateOnly(dashboardStats.retention.d60.cohortEnd)})`
                : '—'
            }
          />
        </Grid>
      </Grid>

      {/* Graphiques */}
      <Grid container spacing={3}>
        {/* Courbe de rétention */}
        <Grid item xs={12}>
          <RetentionChart />
        </Grid>

        {/* DAU / WAU / MAU */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <JoueursActifsSection segment={segment} />
        </Grid>

        {/* Durée des sessions */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <SessionsJeuSection segment={segment} />
        </Grid>

        {/* Paliers d'aventure : niveaux 6, 12, 16 */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <PaliersAventureSection segment={segment} />
        </Grid>

        {/* Nouvelles familles par jour : commencé, terminé, identifié */}
        <Grid item xs={12}>
          <NouvellesFamillesSection segment={segment} />
        </Grid>

        {/* Graphique d'évolution des utilisateurs */}
        <Grid item xs={12}>
          <UsersEvolutionChart />
        </Grid>

        {/* Tunnel d'onboarding */}
        <Grid item xs={12}>
          <OnboardingFunnelChart />
        </Grid>

        {/* Graphique des exercices par type */}
        <Grid item xs={12} md={6}>
          <ExercisesByTypeChart />
        </Grid>

        {/* Espace pour d'autres graphiques futurs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Activité récente" />
            <CardContent>
              {dashboardStats.recentActivity?.slice(0, 5).map((activity, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" fontWeight="bold">
                    {activity.type === 'exercise_created' && '📝 Exercice créé'}
                    {activity.type === 'user_registered' && '👤 Nouvel utilisateur'}
                    {activity.type === 'combat_played' && '⚔️ Combat joué'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {activity.details}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(activity.timestamp).toLocaleString('fr-FR')}
                  </Typography>
                </Box>
              )) || (
                <Typography variant="body2" color="text.secondary">
                  Aucune activité récente
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </AdminLayout>
  );
} 